"use client";

import Link from "next/link";
import Image from "next/image";
import { MainLayout } from "@/components/layout/main-layout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { subscriptionTiers } from "@/lib/mock-data";
import { useState } from "react";
import { Check } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function PricingPage() {
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("annual");

  return (
    <MainLayout>
      <section
        id="pricing"
        className="py-20 bg-gradient-to-b from-muted/30 to-background border-y"
      >
        <div className="container px-4 md:px-6 max-w-screen-2xl mx-auto">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <div className="space-y-2">
              {/* <div className="inline-block rounded-lg bg-muted px-3 py-1 text-sm">
                Transparent
              </div> */}
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
              Pay Only For The Vibes You Ship
              </h2>
              <p className="max-w-[700px] text-muted-foreground md:text-xl">
              Flexible plans for solo hackers and enterprise squads & save up to 20 % when you pay yearly.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mt-8 space-y-3">
            <div className="flex items-center justify-center space-x-4">
              <span
                className={`text-sm transition-colors duration-300 ${
                  billingInterval === "monthly"
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                Monthly
              </span>
              <Switch
                checked={billingInterval === "annual"}
                onCheckedChange={(checked) =>
                  setBillingInterval(checked ? "annual" : "monthly")
                }
                className="transition-transform duration-500 dark:bg-white dark:data-[state=checked]:bg-white dark.data-[state=unchecked]:bg-white [&_span]:dark:bg-gray-800"
              />
              <span
                className={`text-sm transition-colors duration-300 ${
                  billingInterval === "annual"
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                Annual
              </span>
            </div>
            <div className="h-6 flex items-center justify-center">
              {billingInterval === "annual" && (
                <span className="inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-500/20 px-2 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 ring-1 ring-inset ring-emerald-600/20 transition-opacity duration-300">
                  2 months free
                </span>
              )}
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              ...subscriptionTiers,
              {
                id: "enterprise",
                name: "Enterprise",
                description:
                  "Custom solutions for larger teams with advanced needs. Unlimited credits and personalized support.",
                features: [
                  "Everything in AI Docs Pro",
                  "Bulk discounts",
                  "Top priority support",
                  "Custom concurrency limits",
                  "Improved Stealth Proxies",
                ],
                price: 1000,
                popular: false,
              },
            ].map((tier) => (
              <div
                key={tier.id}
                className={`flex flex-col rounded-lg border-2 p-6 shadow-sm bg-background hover:border-primary hover:bg-primary/5 transition-all duration-200 ${
                  tier.id === "ai-docs"
                    ? "border-2 border-blue-500 dark:border-blue-700"
                    : ""
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="space-y-2">
                    <div className="flex items-center gap-4 h-[48px]">
                      <Image 
                        src={tier.name === "Free" ? "/images/features/free-tier.svg" : 
                             tier.name === "AI Docs" ? "/images/features/ai-docs-tier.svg" : 
                             tier.name === "AI Docs Pro" ? "/images/features/ai-docs-tier.svg" : 
                             "/images/features/enterprise.svg"}
                        alt={`${tier.name} Tier`}
                        width={48}
                        height={48}
                        className="w-12 h-12 object-contain"
                      />
                      <h3 className="text-2xl font-bold line-clamp-1">
                        {tier.name === "Free" ? "Docs" : tier.name}
                      </h3>
                      {tier.id === "ai-docs" && (
                        <div className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                          Popular
                        </div>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-2 mb-4">
                      {tier.description}
                    </p>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-baseline h-[60px] justify-center">
                      {tier.id === "enterprise" ? (
                        <span className="text-2xl font-bold">Contact Us</span>
                      ) : (
                        <>
                          <div className="flex flex-col items-center">
                            <div>
                              <span className="text-2xl font-bold">
                                {tier.price === 0
                                  ? "Free Forever"
                                  : formatPrice(
                                      billingInterval === "annual"
                                        ? tier.id === "ai-docs-pro"
                                          ? 500 / 12
                                          : tier.id === "ai-docs"
                                          ? 100 / 12
                                          : tier.price / 12
                                        : tier.price
                                    )}
                              </span>
                              {tier.price > 0 && (
                                <span className="ml-1 text-sm font-bold">
                                  /month
                                </span>
                              )}
                            </div>
                            {billingInterval === "annual" && tier.price > 0 && (
                              <span className="text-sm text-muted-foreground">
                                {formatPrice(
                                  tier.id === "ai-docs-pro"
                                    ? 500
                                    : tier.id === "ai-docs"
                                    ? 100
                                    : tier.price
                                )}
                                /year
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <ul className="mt-6 space-y-2 flex-grow">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-primary" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-6 border-t">
                    <Link
                      href={
                        tier.price === 0 ? "/signup" : "/signup?plan=" + tier.id
                      }
                    >
                      <Button className="w-full" variant="default">
                        {tier.price === 0
                          ? "Get Started"
                          : tier.id === "enterprise"
                          ? "Talk to Sales"
                          : "Subscribe"}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center text-sm text-muted-foreground">
            All AI responses include cited documentation sources. Rate limits
            vary by tier.{" "}
            <Link href="/docs/rate-limits" className="text-primary underline">
              View details here
            </Link>
            .
          </div>
        </div>
      </section>
  {/* Comparison Table */}
  <section className="py-20 px-20 bg-background">
  <div className="container px-4 md:px-6">
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl">
        Compare Plans
      </h2>
      <p className="mt-6 text-muted-foreground text-xl">
        Find the perfect plan for your needs
      </p>
    </div>

    <div className="flex justify-center overflow-x-auto w-full">
      <div className="w-full max-w-6xl mx-auto">
        <table className="w-full border-collapse mx-auto">
          <thead>
            <tr className="border-b">
              <th className="py-6 px-8 font-medium text-left text-lg">Features</th>
              <th className="py-6 px-8 font-medium text-center text-lg">Docs</th>
              <th className="py-6 px-8 font-medium text-center text-lg">AI Docs</th>
              <th className="py-6 px-8 font-medium text-center text-lg">AI Docs Pro</th>
              <th className="py-6 px-8 font-medium text-center text-lg">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">Multi‑LLM support</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
            </tr>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">Ground‑truth citations</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-lg">Limited</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
            </tr>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">Daily GitHub doc sync</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
            </tr>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">OpenAI‑compatible API</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
            </tr>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">Runs on‑prem</td>
              <td className="py-6 px-8 text-center text-lg">Enterprise</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
              <td className="py-6 px-8 text-center text-green-600 text-lg">✓</td>
              <td className="py-6 px-8 text-center text-red-600 text-lg">✗</td>
            </tr>
            <tr className="border-b hover:bg-muted/50">
              <td className="py-6 px-8 text-left text-lg">Cost (10k calls)</td>
              <td className="py-6 px-8 text-center font-medium text-lg">$14.50</td>
              <td className="py-6 px-8 text-center text-lg">Est $100*</td>
              <td className="py-6 px-8 text-center text-lg">$75</td>
              <td className="py-6 px-8 text-center text-lg">$80</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-6 text-sm text-muted-foreground text-left">
          *Approximate, based on $10/user/mo and average usage.
        </p>
      </div>
    </div>
  </div>
</section>

      {/* FAQ Section */}
      <section className="w-full py-20 bg-gradient-to-b from-muted/30 to-background border-y">
        <div className="container px-4 md:px-6 max-w-screen-2xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block rounded-lg bg-muted px-3 py-1 text-sm mb-4">
              Support
            </div>
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-4">Frequently Asked Questions</h2>
            <p className="max-w-[700px] mx-auto text-muted-foreground md:text-lg">
              Got a question? We've got answers. If you have any other questions, feel free to contact us at{" "}
              <a href="mailto:info@genwave.xyz" className="text-primary hover:underline">
                info@genwave.xyz
              </a>
            </p>
          </div>

          <div className="max-w-4xl mx-auto mt-12">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {[
                {
                  question: "What makes Docs4AI different from traditional documentation?",
                  answer: "Docs4AI combines AI-powered documentation with intelligent context awareness, making it perfect for modern development. Our platform automatically understands your codebase, keeps docs in sync with your code, adapts explanations to different expertise levels, and provides interactive examples. With features like real-time updates and smart search, we streamline documentation while ensuring high accuracy."
                },
                {
                  question: "How does Docs4AI ensure accuracy in documentation?",
                  answer: "We employ a multi-layered approach: AI models trained on verified technical content, context-aware analysis of your codebase, continuous validation against your code, and machine learning algorithms that improve over time. Our chatbot maintains accuracy by directly referencing your codebase and documentation, with regular updates improving reliability."
                },
                {
                  question: "What programming languages and frameworks are supported?",
                  answer: "Docs4AI is designed to be framework-agnostic and supports all major technology stacks including React, Angular, Node.js, Python, and many more. Our system continuously updates support for new frameworks and technologies to ensure comprehensive coverage."
                },
                {
                  question: "Can I customize Docs4AI for my specific needs?",
                  answer: "Yes, Docs4AI offers extensive customization options. You can define documentation structure, specify technical terminology, set style guides, and configure AI behavior. The platform seamlessly integrates with your existing tools and workflows, including popular IDEs like VS Code and IntelliJ."
                },
                {
                  question: "How does the AI chatbot assist with documentation?",
                  answer: "Our chatbot provides context-aware responses, handles complex technical queries, and offers detailed explanations with code examples. It integrates with official documentation sources, providing direct links to relevant sections while maintaining conversation context. You can access it right from your development environment for immediate assistance."
                },
                {
                  question: "What are the subscription terms and cancellation policy?",
                  answer: "You can cancel your subscription anytime from your account settings. Once canceled, you won't be charged for the next billing cycle, but you'll retain access until your current period expires. While we don't offer refunds, you can try our free tier without any commitments."
                }
              ].map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`} 
                  className="bg-background rounded-xl border-2 border-blue-500/20 hover:border-blue-500 transition-all duration-200"
                >
                  <AccordionTrigger 
                    className="text-left px-6 py-4 data-[state=open]:bg-blue-50/50 dark:data-[state=open]:bg-blue-950/50 hover:bg-blue-50/50 dark:hover:bg-blue-950/50 hover:no-underline"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 py-4">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}